import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <article className="group cursor-pointer">
        <Link to={`/article/${article.slug}`} className="block">
          <div className="relative overflow-hidden rounded-lg aspect-[16/9]">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 mb-3">
                {article.category}
              </span>
              <h3 className="text-xl md:text-2xl font-heading font-bold text-white leading-snug mb-2 group-hover:text-primary-200 transition-colors">
                {article.title}
              </h3>
              <p className="text-sm text-white/80 line-clamp-2">{article.excerpt}</p>
              <div className="flex items-center gap-4 mt-3 text-xs text-white/60">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-message-2-line"></i>
                  {article.commentCount}
                </span>
                <span>{article.publishDate}</span>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group">
        <Link to={`/article/${article.slug}`} className="flex gap-4 cursor-pointer">
          <div className="w-24 h-24 md:w-28 md:h-28 flex-shrink-0 overflow-hidden rounded-lg">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug mb-1 group-hover:text-primary-600 transition-colors">
              {article.title}
            </h4>
            <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{article.excerpt}</p>
            <div className="flex items-center gap-3 text-xs text-foreground-400">
              <span>{article.category}</span>
              <span>{article.publishDate}</span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300">
      <Link to={`/article/${article.slug}`} className="block cursor-pointer">
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      </Link>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
            {article.category}
          </span>
          {article.isHot && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
              热门
            </span>
          )}
        </div>
        <Link to={`/article/${article.slug}`} className="cursor-pointer">
          <h3 className="text-base font-heading font-bold text-foreground-900 line-clamp-2 leading-snug mb-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-4">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-message-2-line"></i>
              {article.commentCount}
            </span>
          </div>
          <span>{article.publishDate}</span>
        </div>
      </div>
    </article>
  );
}